// Side Banners Configuration
// ============================
// Control both desktop and mobile banners from this single file.
// Set enabled to true/false to show/hide banners.

export const sideBannersConfig = {
  // ===== GLOBAL ON/OFF SWITCH =====
  // Set to false to hide ALL side banners (desktop)
  enabled: true,

  // ===== LEFT BANNER (DESKTOP) =====
  leftBanner: {
    enabled: true,
    
    // Type: 'image' or 'text'
    type: 'image',
    
    // Image source (for type: 'image')
    imageSrc: '/banner_1_.webp',
    imageAlt: 'Создать поездку',
    
    // Text content (for type: 'text')
    title: 'Реклама',
    description: 'Место для вашего баннера',
    backgroundColor: '#667eea',
    textColor: '#ffffff',
    icon: '📢',
    
    // Link - dynamically set based on auth status in component
    // Authorized: /post-create, Unauthorized: /register
    link: 'dynamic', // Special value - handled by component
    linkText: null,
    
    // Always visible - no close button
    dismissible: false,
  },

  // ===== RIGHT BANNER (DESKTOP) =====
  rightBanner: {
    enabled: true,
    
    type: 'image',
    imageSrc: '/banner_2_.webp',
    imageAlt: 'Создать поездку',
    
    title: 'Партнёры',
    description: 'Место для рекламы партнёров',
    backgroundColor: '#f093fb',
    textColor: '#ffffff',
    icon: '🤝',
    
    // Link - dynamically set based on auth status in component
    link: 'dynamic',
    linkText: null,
    
    // Always visible - no close button
    dismissible: false,
  },

  // ===== DESKTOP DISPLAY RULES =====
  minScreenWidth: 1500,
  excludePages: ['/login', '/register', '/chat', '/post-create'],
  showOnPages: [],
  
  // ===== DISPLAY BEHAVIOR (Desktop) =====
  displayBehavior: {
    // Delay before showing banner (milliseconds), 0 = immediate
    showDelay: 0,
    
    // How often to show after dismissal
    // 'session' - once per browser session
    // 'day' - once per day
    // 'week' - once per week
    // 'never' - don't show again after dismissed
    showFrequency: 'session',
  },
};

// ===== MOBILE BANNER CONFIGURATION =====
export const mobileBannerConfig = {
  // Global on/off for mobile banner
  enabled: true,
  
  // Banner content
  content: {
    // Type: 'image' or 'text'
    type: 'text',
    
    // Image (for type: 'image')
    imageSrc: '/banner_1_.webp',
    imageAlt: 'Создать поездку',
    
    // Text content (for type: 'text')
    title: 'Путешествуй вместе!',
    description: 'Путешествуй в компании — создай поездку и найди тех, кто едет туда же',
    backgroundColor: 'linear-gradient(135deg, #4A90E2 0%, #357abd 100%)',
    textColor: '#ffffff',
    icon: '🧳',

    // Call to action
    link: '/companions/create',
    linkText: 'Вместе',
  },
  
  // Display style: 'bottom-sheet', 'sticky-top', 'inline'
  displayStyle: 'bottom-sheet',
  
  // Dismissible - allows user to close
  dismissible: true,
  
  // ===== SMART DISPLAY RULES =====
  displayRules: {
    // Delay before showing (milliseconds)
    // 0 = immediate, 3000 = 3 seconds, etc.
    showDelay: 2000,
    
    // Show frequency after dismissal
    // 'session' - show again next session
    // 'day' - show once per day
    // 'week' - show once per week  
    // 'never' - never show again after dismissed
    showFrequency: 'session',
    
    // Show only after user scrolls X pixels (0 = no scroll required)
    showAfterScroll: 0,
    
    // Maximum times to show per session (0 = unlimited)
    maxShowsPerSession: 1,
  },
  
  // Pages where mobile banner should NOT appear
  excludePages: ['/login', '/register', '/chat', '/post-create', '/companions/create'],
  
  // Pages where mobile banner SHOULD appear (empty = all pages except excluded)
  showOnPages: [],
};

// ===== HELPER FUNCTIONS =====

/**
 * Check if side banners are globally enabled
 */
export const areSideBannersEnabled = () => {
  return sideBannersConfig.enabled;
};

/**
 * Get left banner config
 */
export const getLeftBannerConfig = () => {
  return sideBannersConfig.leftBanner;
};

/**
 * Get right banner config
 */
export const getRightBannerConfig = () => {
  return sideBannersConfig.rightBanner;
};

/**
 * Check if side banners should show on current page
 */
export const shouldShowSideBannersOnPage = (currentPath) => {
  if (!sideBannersConfig.enabled) return false;
  
  // Check exclude list
  if (sideBannersConfig.excludePages.some(path => 
    currentPath === path || currentPath.startsWith(path + '/')
  )) {
    return false;
  }
  
  // Check include list (if empty, show on all pages)
  if (sideBannersConfig.showOnPages.length > 0) {
    return sideBannersConfig.showOnPages.some(path => {
      if (path.includes(':')) {
        const pathPattern = path.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${pathPattern}$`);
        return regex.test(currentPath);
      }
      return currentPath === path || currentPath.startsWith(path + '/');
    });
  }
  
  return true;
};

/**
 * Get minimum screen width for showing banners
 */
export const getMinScreenWidth = () => {
  return sideBannersConfig.minScreenWidth;
};

/**
 * Check if mobile banner should show on current page
 */
export const shouldShowMobileBannerOnPage = (currentPath) => {
  if (!mobileBannerConfig.enabled) return false;
  
  // Check exclude list
  if (mobileBannerConfig.excludePages.some(path => 
    currentPath === path || currentPath.startsWith(path + '/')
  )) {
    return false;
  }
  
  // Check include list (if empty, show on all pages)
  if (mobileBannerConfig.showOnPages.length > 0) {
    return mobileBannerConfig.showOnPages.some(path => {
      if (path.includes(':')) {
        const pathPattern = path.replace(/:[^/]+/g, '[^/]+');
        const regex = new RegExp(`^${pathPattern}$`);
        return regex.test(currentPath);
      }
      return currentPath === path || currentPath.startsWith(path + '/');
    });
  }
  
  return true;
};

// ===== DISMISSAL STORAGE HELPERS =====

const STORAGE_PREFIX = 'banner_';

/**
 * Check if banner can be shown based on frequency settings
 */
export const canShowBanner = (bannerId, frequency) => {
  const storageKey = `${STORAGE_PREFIX}${bannerId}`;
  const dismissedData = localStorage.getItem(storageKey);
  
  if (!dismissedData) return true;
  
  try {
    const { timestamp, type } = JSON.parse(dismissedData);
    const now = Date.now();
    const dismissedAt = new Date(timestamp).getTime();
    
    switch (frequency) {
      case 'session':
        // Session storage handles this - if in localStorage, it's a new session
        // We use sessionStorage for session-based dismissals
        return !sessionStorage.getItem(storageKey);
        
      case 'day':
        const oneDayMs = 24 * 60 * 60 * 1000;
        return (now - dismissedAt) > oneDayMs;
        
      case 'week':
        const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
        return (now - dismissedAt) > oneWeekMs;
        
      case 'never':
        return false;
        
      default:
        return true;
    }
  } catch {
    return true;
  }
};

/**
 * Mark banner as dismissed
 */
export const dismissBanner = (bannerId, frequency) => {
  const storageKey = `${STORAGE_PREFIX}${bannerId}`;
  const data = JSON.stringify({
    timestamp: new Date().toISOString(),
    type: frequency,
  });
  
  if (frequency === 'session') {
    sessionStorage.setItem(storageKey, data);
  } else {
    localStorage.setItem(storageKey, data);
  }
};

/**
 * Get session show count for a banner
 */
export const getSessionShowCount = (bannerId) => {
  const key = `${STORAGE_PREFIX}${bannerId}_count`;
  return parseInt(sessionStorage.getItem(key) || '0', 10);
};

/**
 * Increment session show count
 */
export const incrementSessionShowCount = (bannerId) => {
  const key = `${STORAGE_PREFIX}${bannerId}_count`;
  const count = getSessionShowCount(bannerId) + 1;
  sessionStorage.setItem(key, count.toString());
  return count;
};
